import { useEffect } from 'react';
import { registerGoogleAdsAccounts } from '@archipro-website/tracker';
import {
    GTAG_SCRIPT_ID,
    getOrCreateGtag,
    getTrackingWindow,
    injectHeadScript,
} from '../util/inject-head-script';
import { isConfiguredPixelId } from './is-configured-pixel-id';

interface GoogleAdsProps {
    /** Google Ads account ids in `AW-…` form. */
    accountIds: string[];
}

/**
 * Configures the Google Ads accounts.
 *
 * Two things depend on this. Remarketing audiences are built from the page
 * views it reports, and the conversions `googleAdsTracker` sends need their
 * account configured before `send_to` can address a conversion action on it.
 *
 * gtag.js is shared with GA4 rather than loaded per account: one `config` call
 * per account on the same library is all Google needs, and the bootstrap id in
 * the script URL does not restrict which accounts can be configured.
 *
 * Not mounted at all when the visitor has opted out of sale/sharing, which is
 * what stops `googleAdsTracker` reporting conversions: it sends only for the
 * accounts registered here.
 */
export const GoogleAdsHead: React.FC<GoogleAdsProps> = ({ accountIds }) => {
    // Injected after hydration, for the reason given on `injectHeadScript`.
    useEffect(() => {
        const configured = accountIds.filter(isConfiguredPixelId);
        if (configured.length === 0) {
            return;
        }

        const w = getTrackingWindow();
        const gtag = getOrCreateGtag(w);
        gtag('js', new Date());

        for (const accountId of configured) {
            gtag('config', accountId);
        }

        registerGoogleAdsAccounts(configured);

        injectHeadScript(
            `https://www.googletagmanager.com/gtag/js?id=${configured[0]}`,
            GTAG_SCRIPT_ID,
        );
    }, [accountIds]);

    return null;
};
